/**
 * Telegram Linking Component
 * Allows users to link their Telegram account for receiving trading reports
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  XMarkIcon, 
  CheckCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon 
} from '@heroicons/react/24/outline';
import {
  generateTelegramLinkCode,
  checkTelegramLink,
  unlinkTelegram,
  type TelegramLinkCodeResponse,
} from '../../services/tradingReportsApi';
import { UserAwareCacheKeys } from '../../utils/userAwareCacheKeys';
import { useUserContext } from '../../hooks/useUserContext';
import QRCode from 'react-qr-code';

interface TelegramLinkingProps {
  isLinked: boolean;
  telegramUsername?: string | null;
  linkedAt?: string | null;
  onLinkSuccess: () => void;
}

export const TelegramLinking: React.FC<TelegramLinkingProps> = ({
  isLinked,
  telegramUsername,
  linkedAt,
  onLinkSuccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [linkData, setLinkData] = useState<TelegramLinkCodeResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  
  const queryClient = useQueryClient();
  const { userId } = useUserContext();

  // Generate link code mutation
  const generateLinkMutation = useMutation<TelegramLinkCodeResponse, Error>({
    mutationFn: generateTelegramLinkCode,
    onSuccess: (data) => {
      setLinkData(data);
      setTimeRemaining(data.expires_in_seconds);
      startPolling(data.link_code);
    },
    onError: (error) => {
      toast.error(`Failed to generate link code: ${error.message || 'Unknown error'}`);
    },
  });

  // Unlink Telegram mutation
  const unlinkMutation = useMutation<{ success: boolean; message: string }, Error>({
    mutationFn: unlinkTelegram,
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ 
          queryKey: UserAwareCacheKeys.reportPreferences(userId) 
        });
      }
      toast.success('Telegram account unlinked successfully');
      onLinkSuccess();
    },
    onError: (error) => {
      toast.error(`Failed to unlink: ${error.message || 'Unknown error'}`);
    },
  });

  // Countdown timer
  useEffect(() => {
    if (timeRemaining > 0 && isPolling) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && isPolling) {
      stopPolling();
      toast.error('Link code expired. Please generate a new one.');
    }
  }, [timeRemaining, isPolling]);

  // Polling for link verification
  const startPolling = (linkCode: string) => {
    setIsPolling(true);
    let attempts = 0;
    const maxAttempts = 200; // 10 minutes / 3 seconds = 200 attempts

    const pollInterval = setInterval(async () => {
      attempts++;
      
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        setIsPolling(false);
        return;
      }

      try {
        const result = await checkTelegramLink(linkCode);
        
        if (result.success) {
          clearInterval(pollInterval);
          setIsPolling(false);
          setIsModalOpen(false);
          
          if (userId) {
            queryClient.invalidateQueries({ 
              queryKey: UserAwareCacheKeys.reportPreferences(userId) 
            });
          }
          
          toast.success(`Telegram account linked successfully! @${result.telegram_username}`);
          onLinkSuccess();
        }
      } catch (error) {
        // Continue polling on error (user might not have clicked yet)
        console.debug('Polling check failed:', error);
      }
    }, 3000); // Poll every 3 seconds
  };

  const stopPolling = () => {
    setIsPolling(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    generateLinkMutation.mutate();
  };

  const handleCloseModal = () => {
    stopPolling();
    setIsModalOpen(false);
    setLinkData(null);
    setTimeRemaining(0);
  };

  const handleUnlink = () => {
    if (window.confirm('Are you sure you want to unlink your Telegram account? You will no longer receive reports via Telegram.')) {
      unlinkMutation.mutate();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Link/Unlink Button */}
      <div className="flex items-center justify-between">
        <div>
          {isLinked && telegramUsername ? (
            <div>
              <div className="flex items-center text-sm text-white/90">
                <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
                <span>Linked: <span className="font-mono">@{telegramUsername}</span></span>
              </div>
              {linkedAt && (
                <p className="text-xs text-white/60 mt-1">
                  Linked on {new Date(linkedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-white/70">
              Link your Telegram account to receive reports
            </p>
          )}
        </div>
        
        {isLinked ? (
          <button
            onClick={handleUnlink}
            disabled={unlinkMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-red-400 border border-red-400/30 rounded-lg hover:bg-red-400/10 disabled:opacity-50 transition-colors"
          >
            {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
          </button>
        ) : (
          <button
            onClick={handleOpenModal}
            disabled={generateLinkMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors flex items-center"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
            {generateLinkMutation.isPending ? 'Generating...' : 'Link Telegram'}
          </button>
        )}
      </div>

      {/* Linking Modal */}
      <AnimatePresence>
        {isModalOpen && linkData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-xl shadow-2xl p-6 max-w-md w-full"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">
                  Link Telegram Account
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* QR Code */}
              <div className="bg-white p-4 rounded-lg mb-6 flex justify-center">
                <QRCode value={linkData.deep_link} size={200} />
              </div>

              {/* Instructions */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mr-3">
                    1
                  </div>
                  <p className="text-white/80 text-sm">
                    Scan the QR code with your phone's camera or Telegram app
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mr-3">
                    2
                  </div>
                  <p className="text-white/80 text-sm">
                    Click "Start" in the Telegram bot conversation
                  </p>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold mr-3">
                    3
                  </div>
                  <p className="text-white/80 text-sm">
                    Your account will be linked automatically
                  </p>
                </div>
              </div>

              {/* Deep Link Button */}
              <a
                href={linkData.deep_link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 py-3 text-center text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors mb-4"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 inline-block mr-2" />
                Open in Telegram
              </a>

              {/* Status */}
              {isPolling && (
                <div className="flex items-center justify-center space-x-2 text-sm text-white/70">
                  <ClockIcon className="w-4 h-4 animate-spin" />
                  <span>Waiting for confirmation... ({formatTime(timeRemaining)})</span>
                </div>
              )}

              {/* Link Code (for manual entry) */}
              <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
                <p className="text-xs text-white/60 mb-1">Link Code (manual entry):</p>
                <p className="text-sm text-white font-mono break-all">{linkData.link_code}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
